<template>
    <Page>
        <Container class="max-w-screen-sm">
            <Loading v-if="loading" />

            <div class="overflow-hidden rounded bg-neutral-100 dark:bg-neutral-800">
                <div class="flex items-center justify-between gap-3 bg-neutral-200 p-3 dark:bg-neutral-700">
                    <Button primary outlined size="sm" @click="handleMonthChange(-1)">
                        <Icon fixedWidth icon="chevron-left" />
                    </Button>
                    <div class="flex-1 text-center font-semibold">
                        {{ monthLabel }}
                    </div>
                    <Button primary outlined size="sm" @click="handleMonthChange(1)">
                        <Icon fixedWidth icon="chevron-right" />
                    </Button>
                </div>

                <div class="p-3">
                    <div class="grid grid-cols-7 gap-1 pb-1 text-center text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                        <div v-for="weekday in weekdays" :key="weekday">{{ weekday }}</div>
                    </div>
                    <div class="grid grid-cols-7 gap-1">
                        <button
                            v-for="day in calendarDays"
                            :key="day.date"
                            class="relative aspect-square rounded border text-xs font-semibold"
                            :class="calendarDayClass(day)"
                            @click="handleDaySelect(day.date)"
                        >
                            <span class="absolute left-1.5 top-1">
                                {{ day.label }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="mt-6 flex items-center justify-between gap-3">
                <div class="text-xs font-semibold uppercase text-neutral-500 dark:text-neutral-400">
                    {{ selectedDayLabel }}
                </div>
                <Button v-if="selectedDateIsFuture" primary outlined size="sm" data-cy="add-calendar-workout" @click="handleAddWorkout">
                    Add workout
                </Button>
            </div>

            <div class="mt-3 space-y-4">
                <div v-if="!selectedEntries.length" class="rounded bg-neutral-100 p-4 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                    No entries logged for this day.
                </div>

                <template v-for="entry in selectedEntries" :key="entry.id">
                    <WorkoutResourceListItem
                        v-if="entry.type === 'workout' && entry.workoutResource"
                        :resource="entry.workoutResource"
                    />

                    <article
                        v-else
                        class="overflow-hidden rounded bg-neutral-100 dark:bg-neutral-800"
                    >
                        <div class="flex items-start justify-between gap-3 bg-neutral-200 p-3 dark:bg-neutral-700">
                            <div class="min-w-0">
                                <div class="mb-1 text-xs font-bold uppercase text-brand-primary-500">
                                    {{ entry.type_label }}
                                </div>
                                <div class="font-semibold">{{ entry.title }}</div>
                                <div v-if="entry.subtitle" class="text-sm text-neutral-600 dark:text-neutral-300">
                                    {{ entry.subtitle }}
                                </div>
                            </div>
                            <Button primary outlined size="sm" @click="handleEntryOpen(entry)">
                                Open
                            </Button>
                        </div>

                        <div v-if="entry.rows && entry.rows.length" class="p-3">
                            <div
                                v-for="row in entry.rows"
                                :key="`${entry.id}:${row.label}:${row.value}`"
                                class="flex items-center justify-between gap-3 border-t border-neutral-200 py-2 first:border-t-0 dark:border-neutral-700"
                            >
                                <div class="min-w-0">
                                    <div class="font-semibold">{{ row.label }}</div>
                                    <div v-if="row.detail" class="text-sm text-neutral-600 dark:text-neutral-400">
                                        {{ row.detail }}
                                    </div>
                                </div>
                                <div v-if="row.value" class="shrink-0 text-right font-semibold">
                                    {{ row.value }}
                                </div>
                            </div>
                        </div>

                        <div v-if="entry.notes" class="mx-3 mb-3 rounded bg-neutral-200 px-3 py-2 text-sm text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
                            {{ entry.notes }}
                        </div>
                    </article>
                </template>
            </div>
        </Container>

        <HeadlessDialog :open="templatePickerActive" class="relative z-[80]" @close="templatePickerActive = false">
            <div class="fixed inset-0 bg-black/70" aria-hidden="true" />

            <div class="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel
                    data-cy="calendar-template-modal"
                    class="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded border border-neutral-300 bg-white shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
                >
                    <div class="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 p-4 dark:border-neutral-700">
                        <div>
                            <DialogTitle class="font-semibold">Plan a workout</DialogTitle>
                            <div class="text-sm text-neutral-600 dark:text-neutral-400">{{ selectedDayLabel }}</div>
                        </div>
                        <Button secondary outlined size="sm" @click="templatePickerActive = false">
                            Cancel
                        </Button>
                    </div>

                    <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
                        <Button
                            v-for="template in templates"
                            :key="template.id"
                            secondary
                            outlined
                            block
                            :disabled="schedulingTemplateId !== null"
                            :data-cy="`schedule-template-${template.id}`"
                            @click="handleTemplateSchedule(template)"
                        >
                            {{ template.name }}
                        </Button>
                    </div>
                </DialogPanel>
            </div>
        </HeadlessDialog>
    </Page>
</template>

<script>
import {defineComponent} from 'vue';
import {mapActions} from 'vuex';
import {
  Dialog as HeadlessDialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/vue';
import moment from 'moment-timezone';
import {ResourceStore, Workout} from '@/internal';
import CalendarService from '@/domain/Calendar/CalendarService.js';
import WorkoutResourceListItem from '@/components/Workout/ResourceListItem.vue';
import {calendarIsEnabled} from '@/utils/settings.js';
import {capturePostHogEvent} from '@/utils/posthog.js';

const calendarService = new CalendarService();

export default defineComponent({
  name: 'CalendarIndex',
  components: {
    DialogPanel,
    DialogTitle,
    HeadlessDialog,
    WorkoutResourceListItem,
  },
  created() {
    if (!this.calendarEnabled) {
      this.$router.replace({name: 'workout/index'});

      return;
    }

    this.applyRouteDate();
    this.loadTemplates();
    this.loadData();
  },
  watch: {
    '$route.query.date'(date) {
      const monthChanged = this.applyRouteDate(date);

      if (monthChanged) {
        this.loadData();
      }
    },
  },
  data() {
    return {
      entries: [],
      loading: false,
      month: moment().startOf('month'),
      schedulingTemplateId: null,
      selectedDate: moment().format('YYYY-MM-DD'),
      templatePickerActive: false,
      weekdays: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    };
  },
  computed: {
    calendarDays() {
      const start = this.month.clone().startOf('month').startOf('week');

      return Array.from({length: 42}, (_, index) => {
        const date = start.clone().add(index, 'days');
        const isoDate = date.format('YYYY-MM-DD');

        return {
          date: isoDate,
          inMonth: date.month() === this.month.month(),
          label: date.format('D'),
          selected: isoDate === this.selectedDate,
          today: isoDate === moment().format('YYYY-MM-DD'),
          total: this.entriesByDate[isoDate]?.length || 0,
        };
      });
    },
    entriesByDate() {
      return this.entries.reduce((dates, entry) => {
        dates[entry.date] = dates[entry.date] || [];
        dates[entry.date].push(entry);

        return dates;
      }, {});
    },
    monthLabel() {
      return this.month.format('MMM YYYY');
    },
    selectedDayLabel() {
      return moment(this.selectedDate).format('ddd, MMM D, YYYY');
    },
    selectedEntries() {
      return this.entriesByDate[this.selectedDate] || [];
    },
    selectedDateIsFuture() {
      return moment.tz(this.selectedDate, 'YYYY-MM-DD', this.timezone)
        .isAfter(moment().tz(this.timezone).startOf('day'));
    },
    templates() {
      return [...(this.$store.getters['template/all'] || [])]
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    timezone() {
      return this.user.timezone || moment.tz.guess();
    },
    calendarEnabled() {
      return calendarIsEnabled(this.user);
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  methods: {
    ...mapActions({
      templateIndex: 'template/index',
    }),
    applyRouteDate(date = this.$route.query.date) {
      const routeDate = this.normalizedRouteDate(date);

      if (!routeDate) {
        return false;
      }

      const currentMonth = this.month.format('YYYY-MM');
      const routeMonth = moment(routeDate).startOf('month');

      this.selectedDate = routeDate;
      this.month = routeMonth;

      return currentMonth !== routeMonth.format('YYYY-MM');
    },
    calendarDayClass(day) {
      return [
        !day.inMonth ? 'opacity-40' : null,
        day.total === 0 ? 'border-neutral-700 bg-neutral-900' : null,
        day.total === 1 ? 'border-brand-primary-400/30 bg-brand-primary-400/10' : null,
        day.total === 2 ? 'border-brand-primary-400/40 bg-brand-primary-400/20' : null,
        day.total >= 3 ? 'border-brand-primary-400/50 bg-brand-primary-400/30' : null,
        day.selected ? '!border-brand-primary-400 ring-1 ring-brand-primary-400' : null,
        day.today ? 'text-brand-primary-400' : null,
      ];
    },
    calendarEventProperties(extra = {}) {
      const selectedEntries = this.entriesByDate[this.selectedDate] || [];
      const entryTypes = this.entries.reduce((types, entry) => {
        types[entry.type] = (types[entry.type] || 0) + 1;

        return types;
      }, {});

      return {
        calendar_month: this.month.format('YYYY-MM'),
        selected_date: this.selectedDate,
        selected_day_entry_count: selectedEntries.length,
        selected_day_has_entries: selectedEntries.length > 0,
        month_entry_count: this.entries.length,
        month_has_entries: this.entries.length > 0,
        workout_entry_count: entryTypes.workout || 0,
        log_entry_count: entryTypes.log_entry || 0,
        ...extra,
      };
    },
    capture(event, properties = {}) {
      capturePostHogEvent(event, this.calendarEventProperties(properties));
    },
    handleAddWorkout() {
      this.capture('calendar_workout_add_started');
      this.templatePickerActive = true;
    },
    handleTemplateSchedule(template) {
      this.schedulingTemplateId = template.id;

      const workoutAt = moment.tz(`${this.selectedDate} 12:00`, 'YYYY-MM-DD HH:mm', this.timezone)
        .utc()
        .toISOString();

      return calendarService.schedule(template.id, workoutAt).then(() => {
        this.capture('calendar_workout_planned', {
          template_id: template.id,
        });
        this.templatePickerActive = false;
        window.app.snackbar({
          type: 'success',
          message: 'Workout planned',
        });

        return this.loadData();
      }).catch((error) => {
        window.app.snackbarError(error);
      }).finally(() => {
        this.schedulingTemplateId = null;
      });
    },
    handleDaySelect(date) {
      const day = this.calendarDays.find((calendarDay) => calendarDay.date === date);

      this.capture('calendar_day_selected', {
        date,
        date_month: moment(date).format('YYYY-MM'),
        day_entry_count: day?.total || 0,
        day_has_entries: (day?.total || 0) > 0,
        in_displayed_month: day?.inMonth ?? false,
        previous_selected_date: this.selectedDate,
      });

      this.persistSelectedDate(date);
    },
    handleEntryOpen(entry) {
      this.capture('calendar_entry_opened', {
        entry_id: entry.id,
        entry_type: entry.type,
        entry_date: entry.date,
        entry_row_count: entry.rows?.length || 0,
        entry_has_notes: !!entry.notes,
      });

      this.$router.push(entry.href);
    },
    handleMonthChange(direction) {
      const date = this.month.clone().add(direction, 'month').startOf('month').format('YYYY-MM-DD');

      this.capture('calendar_month_changed', {
        direction: direction > 0 ? 'next' : 'previous',
        target_month: moment(date).format('YYYY-MM'),
      });

      this.persistSelectedDate(date);
    },
    loadData() {
      this.loading = true;

      return calendarService.index({
        start: this.month.clone().startOf('month').format('YYYY-MM-DD'),
        end: this.month.clone().endOf('month').format('YYYY-MM-DD'),
      }).then((response) => {
        this.entries = (response.data.data.entries || []).map((entry) => {
          if (entry.type !== 'workout' || !entry.workout) {
            return entry;
          }

          const ids = ResourceStore.processData(
            Workout.key,
            this.$store.commit.bind(this.$store),
            entry.workout
          );

          return {
            ...entry,
            workoutResource: this.$store.getters['workout/show'](ids[0]),
          };
        });

        this.capture('calendar_viewed');

        if (!this.selectedEntries.length) {
          this.capture('calendar_empty_day_viewed');
        }
      }).finally(() => {
        this.loading = false;
      });
    },
    loadTemplates() {
      return this.templateIndex();
    },
    normalizedRouteDate(date) {
      if (!date || !moment(date, 'YYYY-MM-DD', true).isValid()) {
        return null;
      }

      return moment(date, 'YYYY-MM-DD').format('YYYY-MM-DD');
    },
    persistSelectedDate(date) {
      const selectedDate = this.normalizedRouteDate(date);

      if (!selectedDate) {
        return;
      }

      if (this.$route.query.date === selectedDate) {
        const monthChanged = this.applyRouteDate(selectedDate);

        if (monthChanged) {
          this.loadData();
        }

        return;
      }

      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          date: selectedDate,
        },
      });
    },
  },
});
</script>
